import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ChevronUp, User, LogIn, LogOut, ShoppingBag, UserCircle, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Menu structure with dropdowns
const menuItems = {
  'Home': { 
    path: '/', 
    dropdown: false 
  },

  'Prodotti': { 
    path: '/prodotti', 
    dropdown: true,
    items: {
      'E-book gratuito - Cucina Consapevole Facile': {
        content: <>
          <span className="font-bold">📖 E-book:</span> Cucina Consapevole Facile
        </>,
        path: '/ebook-cucina-consapevole-facile'
      },
      'E-book - Il cibo che purifica': {
        content: <>
        <span className="font-bold">📚 E-book:</span> Il Cibo che Purifica
        </>,
        path: '/il-cibo-che-purifica'
      },
      'Meditazione - Il Nyasa: Ritrova la calma dentro di te': {
        content: <>
        <span className="font-bold">🔊 Meditazione:</span> Il Nyasa: Ritrova la calma dentro di te
        </>,
        path: '/il-nyasa'
      }
    }
  },

  'Chi sono': { 
    path: '/about', 
    dropdown: false,
  },

  'Contatti': { 
    path: '/contatti', 
    dropdown: true,
    items: {
      'Contattami': '/contattami',
      'Newsletter': '/newsletter',
    }
  }
};

const CustomButton = ({ children, isScrolled, className = '', to, onClick, eventName, eventData = {}, outline = false }) => {
  const umamiProps = eventName ? 
    Object.entries(eventData).reduce((acc, [key, value]) => {
      acc[`data-umami-event-${key}`] = value;
      return acc;
    }, { 'data-umami-event': eventName }) : 
    {};

  return (
    <div className={`inline-block button ${className}`} onClick={onClick}>
      <Link
        to={to}
        className={`px-5 py-3 md:px-6 md:py-4 lg:px-8 lg:py-5 rounded-md md:rounded-lg transition-colors duration-300 md:text-base ${
          outline
            ? `border-2 ${isScrolled ? 'border-green text-green hover:bg-green hover:text-white' : 'border-beige text-text-dark hover:bg-beige hover:text-text-dark'}`
            : isScrolled
              ? 'bg-green text-text-light hover:bg-accent hover:text-white'
              : 'bg-beige text-text-dark hover:bg-accent hover:text-white'
        } ${className.includes('text-2xl') || className.includes('text-3xl') ? 'px-6 py-4 font-bold text-center w-64' : ''}`}
        {...umamiProps}
      >
        {children}
      </Link>
    </div>
  );
};

// Desktop Dropdown menu component with left alignment
const DropdownMenu = ({ items, isOpen, isScrolled, closeDropdown, setIsHovering }) => {
  return (
    <div 
      className={`absolute left-0 mt-2 py-3 bg-beige-light rounded-md shadow-lg transition-all duration-300 ${
        isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
      }`}
      style={{ minWidth: '320px' }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => {
        setIsHovering(false);
        setTimeout(() => {
          closeDropdown();
        }, 150);
      }}
    >
      {Object.entries(items).map(([key, value]) => {
        const isComplexItem = typeof value === 'object' && value !== null && 'content' in value && 'path' in value;
        const content = isComplexItem ? value.content : key;
        const path = isComplexItem ? value.path : value;
        
        return (
          <Link
            key={key}
            to={path}
            className={`block px-6 py-3 text-left text-body hover:bg-beige hover:text-accent transition-colors duration-200 ${
              isScrolled ? 'text-green-dark' : 'text-green-dark'
            }`}
            onClick={closeDropdown}
          >
            {content}
          </Link>
        );
      })}
    </div>
  );
};

// Enhanced Mobile dropdown component with left alignment
const MobileDropdown = ({ name, items, isOpen, toggleDropdown, closeMenu }) => {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  return (
    <div className="w-full max-w-md">
      <button 
        className="w-full flex items-center justify-start text-2xl font-bold text-white mb-2 group relative overflow-hidden py-2 px-4"
        onClick={toggleDropdown}
      >
        <span className="relative z-10 flex items-center">
          {name}
          <div className={`ml-2 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
            <ChevronDown size={20} />
          </div>
        </span>
        <div className="absolute inset-0 bg-white/10 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left rounded-lg" />
      </button>
      
      <div 
        className={`transition-all duration-500 ease-out overflow-hidden ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="flex flex-col items-start space-y-2 pt-2 pb-4 pl-8">
          {Object.entries(items).map(([subName, value], index) => {
            const isComplexItem = typeof value === 'object' && value !== null && 'content' in value && 'path' in value;
            const content = isComplexItem ? value.content : subName;
            const path = isComplexItem ? value.path : value;
            
            return (
              <Link
                key={subName}
                to={path}
                className={`text-lg text-white/90 hover:text-white px-4 py-2 rounded-lg transition-all duration-300 hover:bg-white/10 transform hover:translate-x-2 text-left ${
                  isAnimating && isOpen ? 'animate-fadeInUp' : ''
                }`}
                style={{ animationDelay: `${index * 50}ms` }}
                onClick={closeMenu}
              >
                <span className="relative z-10">{content}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const Header = ({ iconPath }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileOpenDropdowns, setMobileOpenDropdowns] = useState({});
  const location = useLocation();
  const dropdownRefs = useRef({});
  const userDropdownRef = useRef(null);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [isHovering, setIsHovering] = useState(false);

  const { user, signOut } = useAuth();
  const isLoggedIn = !!user;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handleScroll);

    const handleClickOutside = (event) => {
      if (openDropdown && dropdownRefs.current[openDropdown] && 
          !dropdownRefs.current[openDropdown].contains(event.target)) {
        setOpenDropdown(null);
      }

      if (openDropdown === 'user' && userDropdownRef.current && 
          !userDropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  const handleLogout = async () => {
    try {
      console.log("Starting logout process...");
      
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Logout timed out')), 10000)
      );
      
      const result = await Promise.race([
        signOut(),
        timeoutPromise
      ]);
      
      console.log("Logout result:", result);
      
      if (result && result.error) {
        console.error("Logout returned error:", result.error);
        throw new Error(typeof result.error === 'string' ? result.error : "Logout failed");
      }
      
      setOpenDropdown(null);
      setIsMenuOpen(false);
      
      setTimeout(() => {
        console.log("Forcing page reload after logout");
        window.location.href = '/';
      }, 500);
      
    } catch (error) {
      console.error("Logout failed with exception:", error);
      alert("Logout failed. Please try again or refresh the page.");
    }
  };

  useEffect(() => {
    closeMenu();
  }, [location]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    document.body.style.overflow = isMenuOpen ? 'auto' : 'hidden';
    setMobileOpenDropdowns({});
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    document.body.style.overflow = 'auto';
    setMobileOpenDropdowns({});
  };

  const handleDropdownToggle = (name, isHover = false) => {
    if (isHover) {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        setHoverTimeout(null);
      }
      setIsHovering(true);
      setOpenDropdown(name);
    } else {
      setOpenDropdown(openDropdown === name ? null : name);
    }
  };

  const handleMouseEnter = (name) => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setIsHovering(true);
    handleDropdownToggle(name, true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    
    const timeout = setTimeout(() => {
      if (!isHovering) {
        setOpenDropdown(null);
      }
    }, 150);
    
    setHoverTimeout(timeout);
  };

  const toggleMobileDropdown = (name) => {
    setMobileOpenDropdowns(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  return (
    <>
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.5s ease-out forwards;
        }
        
        .animate-slideInRight {
          animation: slideInRight 0.4s ease-out forwards;
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
        
        .animate-pulse {
          animation: pulse 2s ease-in-out infinite;
        }
        
        .glass-morphism {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }
        
        .gradient-border {
          position: relative;
          background: linear-gradient(90deg, #fff 0%, #fff 100%);
          background-clip: padding-box;
          border: 2px solid transparent;
        }
        
        .gradient-border::before {
          content: '';
          position: absolute;
          inset: -2px;
          background: linear-gradient(90deg, #4ade80 0%, #22c55e 50%, #16a34a 100%);
          border-radius: inherit;
          z-index: -1;
        }
      `}</style>
      
      <header 
        className={`sticky top-0 z-50 transition-all duration-500 w-full
        ${isScrolled ? 'bg-beige-light shadow-md' : 'bg-beige-light'} 
        h-16 sm:h-20 md:h-24 lg:h-[100px] py-2 flex items-center`}
      >
        <div className="container mx-auto px-4 w-full">
          <div className="flex justify-between items-center">
            {/* Logo and Name */}
            <div className="flex items-center">
              <div className="bg-transparent w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 lg:w-15 lg:h-15 rounded-full mr-2 flex items-center justify-center transition-transform duration-300 ease-in-out hover:scale-110">
                <img 
                  src={iconPath}
                  alt="Alessandra Filzoli Icon"
                  className="w-full h-full object-contain"
                />
              </div>
              <Link to="/" className={`text-xl sm:text-2xl md:text-2xl lg:text-3xl flex items-center font-black leading-tight transition-colors duration-300 ${isScrolled ? 'text-text-dark hover:text-accent' : 'text-text-dark hover:text-accent'}`}>
                Alessandra Filzoli
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className={`hidden md:flex md:space-x-4 lg:space-x-6 items-center text-base lg:text-lg`}>
              {Object.entries(menuItems).map(([name, item]) => (
                <div 
                key={name} 
                className="relative"
                ref={el => dropdownRefs.current[name] = el}
                onMouseEnter={() => handleMouseEnter(name)}
                onMouseLeave={handleMouseLeave}
              >
                {item.dropdown ? (
                  <>
                    <button
                      className={`flex items-center transition-colors body_tile duration-300 hover:text-accent ${
                        location.pathname === item.path || location.pathname.startsWith(item.path + '/') 
                          ? 'font-bold text-accent' 
                          : isScrolled ? 'text-green-dark' : 'text-green-dark'
                      }`}
                      onClick={() => handleDropdownToggle(name)}
                    >
                      {name}
                      {openDropdown === name ? 
                        <ChevronUp className="ml-1" size={16} /> : 
                        <ChevronDown className="ml-1" size={16} />
                      }
                    </button>
                    <DropdownMenu 
                      items={item.items} 
                      isOpen={openDropdown === name}
                      isScrolled={isScrolled}
                      closeDropdown={() => setOpenDropdown(null)}
                      setIsHovering={setIsHovering}
                    />
                  </>
                ) : (
                  <Link
                    to={item.path}
                    className={`transition-colors body_tile duration-300 hover:text-accent ${
                      location.pathname === item.path ? 'font-bold text-accent' : 
                      isScrolled ? 'text-green-dark' : 'text-green-dark'
                    }`}
                  >
                    {name}
                  </Link>
                )}
              </div>
              ))}
              <CustomButton isScrolled={isScrolled} to="/prenota" eventName="Prenota Button Click">
                Prenota
              </CustomButton>

              {/* User Account Area */}
              <div className="relative ml-2" ref={userDropdownRef}>
                {isLoggedIn ? (
                  <>
                    <button
                      className={`flex items-center justify-center transition-colors duration-300 hover:text-accent p-2 rounded-full ${
                        openDropdown === 'user' ? 'bg-beige text-accent' : 
                        isScrolled ? 'text-green-dark' : 'text-green-dark'
                      }`}
                      onClick={() => handleDropdownToggle('user')}
                      onMouseEnter={() => handleDropdownToggle('user')}
                      aria-label="User menu"
                    >
                      <User size={24} />
                    </button>
                    <div 
                      className={`absolute right-0 mt-2 py-2 bg-beige-light rounded-md shadow-lg transition-all duration-300 ${
                        openDropdown === 'user' ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
                      }`}
                      style={{ minWidth: '180px' }}
                    >
                      <Link
                        to="/profilo"
                        className={`flex items-center px-4 py-2 text-sm hover:bg-beige hover:text-accent transition-colors duration-200 ${
                          isScrolled ? 'text-green-dark' : 'text-green-dark'
                        }`}
                        onClick={() => setOpenDropdown(null)}
                      >
                        <UserCircle size={18} className="mr-2" />
                        Il mio profilo
                      </Link>
                      <Link
                        to="/contenuti"
                        className={`flex items-center px-4 py-2 text-sm hover:bg-beige hover:text-accent transition-colors duration-200 ${
                          isScrolled ? 'text-green-dark' : 'text-green-dark'
                        }`}
                        onClick={() => setOpenDropdown(null)}
                      >
                        <ShoppingBag size={18} className="mr-2" />
                        I miei acquisti
                      </Link>
                      <button
                        className={`flex items-center w-full text-left px-4 py-2 text-sm hover:bg-beige hover:text-accent transition-colors duration-200 ${
                          isScrolled ? 'text-green-dark' : 'text-green-dark'
                        }`}
                        onClick={handleLogout}
                      >
                        <LogOut size={18} className="mr-2" />
                        Logout
                      </button>
                    </div>
                  </>
                ) : (
                  <Link
                    to="/login"
                    className={`flex items-center transition-colors duration-300 hover:text-accent ${
                      isScrolled ? 'text-green-dark' : 'text-green-dark'
                    }`}
                  >
                    <LogIn size={20} className="mr-1" />
                    <span>Accedi</span>
                  </Link>
                )}
              </div>
            </nav>

            {/* Enhanced Mobile Menu Button */}
            <div className="md:hidden flex items-center">
              <button 
                onClick={toggleMenu} 
                className={`relative hover:text-accent z-50 p-2 group ${isMenuOpen ? 'text-white' : 'text-green-dark'}`}
                aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              >
                <div className="relative">
                  <div className={`transition-all duration-500 transform ${isMenuOpen ? 'rotate-180 scale-110' : ''}`}>
                    {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
                  </div>
                  {!isMenuOpen && (
                    <div className="absolute inset-0 rounded-full bg-accent/20 scale-0 group-hover:scale-150 transition-transform duration-300 -z-10" />
                  )}
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Enhanced Full-screen mobile menu */}
        <div 
          className={`fixed inset-0 z-40 flex flex-col transition-all duration-500 ease-out ${
            isMenuOpen 
              ? 'opacity-100 pointer-events-auto' 
              : 'opacity-0 pointer-events-none'
          } md:hidden`}
        >
          {/* Gradient Background */}
          <div className={`absolute inset-0 bg-gradient-to-br from-green-dark via-green-dark to-accent/80 transition-transform duration-500 ${
            isMenuOpen ? 'scale-100' : 'scale-95'
          }`} />
          
          {/* Glass morphism overlay */}
          <div className="absolute inset-0 glass-morphism opacity-10" />
          
          {/* Animated background elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className={`absolute -top-20 -left-20 w-64 h-64 bg-white/5 rounded-full blur-3xl ${isMenuOpen ? 'animate-pulse' : ''}`} />
            <div className={`absolute -bottom-20 -right-20 w-80 h-80 bg-accent/10 rounded-full blur-3xl ${isMenuOpen ? 'animate-pulse' : ''}`} style={{ animationDelay: '1s' }} />
          </div>
          
          {/* Content */}
          <div className="relative z-10 flex flex-col h-full">
            {/* Header area with logo */}
            <div className={`flex justify-between items-center px-6 pt-8 ${isMenuOpen ? 'animate-fadeIn' : ''}`}>
              <div className="flex items-center">
                <div className="bg-white/20 w-12 h-12 rounded-full mr-3 flex items-center justify-center backdrop-blur-sm shadow-lg">
                  <img 
                    src={iconPath}
                    alt="Alessandra Filzoli Icon"
                    className="w-10 h-10 object-contain"
                  />
                </div>
                <div className="text-xl font-bold text-white drop-shadow-lg">
                  Alessandra Filzoli
                </div>
              </div>
            </div>

            {/* Navigation items */}
            <nav className="flex-grow flex flex-col justify-center items-start px-6 space-y-6 relative">
              {/* Background pattern */}
              <div className="absolute inset-0 opacity-5">
                <div className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full blur-3xl" />
                <div className="absolute bottom-20 right-10 w-40 h-40 bg-accent rounded-full blur-3xl" />
              </div>
              
              {Object.entries(menuItems).map(([name, item], index) => (
                <div 
                  key={name} 
                  className={`w-full ${
                    isMenuOpen ? 'animate-fadeInUp' : ''
                  }`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {item.dropdown ? (
                    <MobileDropdown 
                      name={name}
                      items={item.items}
                      isOpen={mobileOpenDropdowns[name] || false}
                      toggleDropdown={() => toggleMobileDropdown(name)}
                      closeMenu={closeMenu}
                    />
                  ) : (
                    <Link
                      to={item.path}
                      className="relative text-2xl font-bold text-white hover:text-white transition-all duration-300 group block py-2 px-4"
                      onClick={closeMenu}
                    >
                      <span className="relative z-10">{name}</span>
                      <div className="absolute inset-0 bg-white/10 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left rounded-lg" />
                    </Link>
                  )}
                </div>
              ))}
              
              {/* Prenota Button */}
              <div className={`mt-8 w-full ${isMenuOpen ? 'animate-fadeInUp' : ''}`} style={{ animationDelay: '400ms' }}>
                <Link
                  to="/prenota"
                  className="relative inline-flex items-center justify-center w-full px-8 py-4 text-xl font-bold text-green-dark bg-white rounded-full shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 group overflow-hidden gradient-border"
                  onClick={closeMenu}
                  data-umami-event="Prenota Mobile Button Click"
                >
                  <span className="relative z-10 transition-colors duration-300 group-hover:text-white">Prenota</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-accent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left rounded-full" />
                </Link>
              </div>

              {/* Mobile User Account Area */}
              <div className={`w-full mt-8 ${isMenuOpen ? 'animate-fadeInUp' : ''}`} style={{ animationDelay: '500ms' }}>
                <div className="w-full">
                  {isLoggedIn ? (
                    <>
                      <div className="flex items-center justify-center mb-6 p-4 rounded-full bg-white/20 backdrop-blur-sm shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 w-16 h-16 mx-auto">
                        <User size={32} className="text-white" />
                      </div>
                      <div className="flex flex-col items-start space-y-4 w-full">
                        <Link
                          to="/profilo"
                          className="flex items-center text-lg text-white/90 hover:text-white px-4 py-2 rounded-lg transition-all duration-300 hover:bg-white/10 transform hover:translate-x-2 group"
                          onClick={closeMenu}
                        >
                          <UserCircle size={20} className="mr-2 group-hover:rotate-12 transition-transform duration-300" />
                          Il mio profilo
                        </Link>
                        <Link
                          to="/contenuti"
                          className="flex items-center text-lg text-white/90 hover:text-white px-4 py-2 rounded-lg transition-all duration-300 hover:bg-white/10 transform hover:translate-x-2 group"
                          onClick={closeMenu}
                        >
                          <ShoppingBag size={20} className="mr-2 group-hover:rotate-12 transition-transform duration-300" />
                          I miei acquisti
                        </Link>
                        <button
                          className="flex items-center text-lg text-white/90 hover:text-white px-4 py-2 rounded-lg transition-all duration-300 hover:bg-white/10 transform hover:translate-x-2 group"
                          onClick={() => {
                            handleLogout();
                            closeMenu();
                          }}
                        >
                          <LogOut size={20} className="mr-2 group-hover:-translate-x-1 transition-transform duration-300" />
                          Logout
                        </button>
                      </div>
                    </>
                  ) : (
                    <Link
                      to="/login"
                      className="flex items-center justify-center text-xl font-semibold text-white hover:text-white px-8 py-3 rounded-full transition-all duration-300 hover:bg-white/10 transform hover:scale-105 glass-morphism mx-auto"
                      onClick={closeMenu}
                    >
                      <LogIn size={24} className="mr-2" />
                      Accedi
                    </Link>
                  )}
                </div>
              </div>
            </nav>
            
            {/* Bottom decorative element */}
            <div className="h-20 relative">
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                <div className="w-2 h-2 bg-white/30 rounded-full animate-pulse" />
                <div className="w-2 h-2 bg-white/30 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                <div className="w-2 h-2 bg-white/30 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;